import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  DashboardClientScope,
  DashboardRecentAssignmentItem
} from 'src/app/models/DashboardMetrics';
import { StaffAssignmentTypeCodes } from 'src/app/models/WorkforceStaffModels';
import { DashboardMetricsService } from 'src/app/services/dashboard-metrics.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { getErrorMessage } from 'src/app/utils/error-message';

@UntilDestroy()
@Component({
  selector: 'vex-recently-assigned-widget',
  templateUrl: './recently-assigned-widget.component.html',
  styleUrls: ['./recently-assigned-widget.component.scss']
})
export class RecentlyAssignedWidgetComponent implements OnChanges {
  @Input() scope: DashboardClientScope = 'mine';

  loading = false;
  items: DashboardRecentAssignmentItem[] = [];

  constructor(
    private dashboardMetricsService: DashboardMetricsService,
    private snackbarService: SnackbarService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['scope']) {
      this.loadAssignments();
    }
  }

  get title(): string {
    return this.scope === 'all' ? 'Recently Assigned' : 'Recently Assigned to Me';
  }

  get emptyMessage(): string {
    return this.scope === 'all'
      ? 'No account or billing manager assignments in the last 30 days.'
      : 'No clients assigned to you in the last 30 days.';
  }

  roleBadge(item: DashboardRecentAssignmentItem): string {
    switch (item.assignmentTypeCode) {
      case StaffAssignmentTypeCodes.SecondaryAccountManager:
        return 'New Co-AM';
      case StaffAssignmentTypeCodes.PrimaryBillingManager:
        return 'New BM';
      case StaffAssignmentTypeCodes.SecondaryBillingManager:
        return 'New Co-BM';
      default:
        return 'New AM';
    }
  }

  isBillingAssignment(item: DashboardRecentAssignmentItem): boolean {
    return item.assignmentTypeCode === StaffAssignmentTypeCodes.PrimaryBillingManager
      || item.assignmentTypeCode === StaffAssignmentTypeCodes.SecondaryBillingManager;
  }

  private loadAssignments(): void {
    this.loading = true;
    this.dashboardMetricsService.getRecentAssignments(this.scope)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (result) => {
          this.items = result.items ?? [];
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          this.items = [];
          this.snackbarService.open(
            getErrorMessage(err, 'Failed to load recently assigned clients.'),
            'error'
          );
        }
      });
  }
}
